import { createContext, useContext, useEffect, type MouseEvent } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AccessNotice, ChartIndex, Skeleton, type Patient as DsPatient } from '@etnas/design-system';
import { isApiError, patientsApi, type AccessCheck, type Patient } from '@/api';
import { qk } from '@/lib/queryKeys';
import { toDsPatient, sexShort } from '@/lib/patient';
import { ShellFrame } from '../shell/Shell';
import { usePatientSlots, type SlotNo } from '../patientSlots';
import { useAuth } from '../auth';
import { PatientBannerX } from '@/components/PatientBannerX';
import { ErrorState } from '@/components/States';
import { ChartAccessDenied, RestrictedNotice } from '@/components/BreakGlass';
import { WORKSPACES } from '../workspaces';
import { purposeForRole } from '@/api/modules/auth';
import { useChartPurpose } from './useChartPurpose';
import { RoomingClaims } from '@/features/nursing/RoomingClaims';

export interface ChartContextValue {
  patientId: string;
  patient: Patient;
  etag: string | null;
  access: AccessCheck | null;
  slot: SlotNo;
  /** DS-shaped patient for PatientConfirmLine / confirmations */
  ds: DsPatient;
  /** Whether a chart section id is restricted for the caller */
  isRestricted: (section: string) => boolean;
}

const ChartCtx = createContext<ChartContextValue | null>(null);

export function useChart(): ChartContextValue {
  const c = useContext(ChartCtx);
  if (!c) throw new Error('useChart outside ChartLayout');
  return c;
}

type Section = { id: string; label: string; icon: string } | { divider: string };

/** Chart index sections per role (guideline 60: what changes per role is the rail, landing and index). */
export function chartSections(role: string): Section[] {
  if (role === 'nurse') {
    return [
      { id: 'summary', label: 'Summary', icon: 'home' },
      { divider: 'This visit' },
      { id: 'rooming', label: 'Rooming', icon: 'vitals' },
      { id: 'allergies', label: 'Allergies', icon: 'urgent' },
      { id: 'medrec', label: 'Medication review', icon: 'pill' },
      { id: 'administer', label: 'Administer', icon: 'stethoscope' },
      { id: 'orders', label: 'Orders', icon: 'orders' },
      { divider: 'Record' },
      { id: 'vitals', label: 'Vitals', icon: 'trend' },
      { id: 'medications', label: 'Medications', icon: 'pill' },
      { id: 'problems', label: 'Problems', icon: 'layers' },
      { id: 'results', label: 'Results', icon: 'flask' },
      { id: 'notes', label: 'Notes', icon: 'note' },
      { id: 'documents', label: 'Documents', icon: 'folder' },
      { id: 'care-team', label: 'Care team', icon: 'users' },
    ];
  }
  return [
    { id: 'summary', label: 'Summary', icon: 'home' },
    { id: 'timeline', label: 'Timeline', icon: 'history' },
    { divider: 'Record' },
    { id: 'problems', label: 'Problems', icon: 'layers' },
    { id: 'medications', label: 'Medications', icon: 'pill' },
    { id: 'allergies', label: 'Allergies', icon: 'urgent' },
    { id: 'vitals', label: 'Vitals', icon: 'trend' },
    { id: 'results', label: 'Results', icon: 'flask' },
    { id: 'notes', label: 'Notes', icon: 'note' },
    { id: 'orders', label: 'Orders', icon: 'orders' },
    { id: 'prescriptions', label: 'Prescriptions', icon: 'pill' },
    { id: 'documents', label: 'Documents', icon: 'folder' },
    { divider: 'Care' },
    { id: 'care-team', label: 'Care team', icon: 'users' },
    { id: 'care-gaps', label: 'Care gaps and plan', icon: 'check' },
    { id: 'referrals', label: 'Referrals', icon: 'arrow-right' },
  ];
}

/**
 * Patient chart frame: persistent banner (zone 5), chart index (zone 6) with restricted flags, canvas outlet,
 * and route-provided drawer/tray. Guards leaving with unsaved work and keeps the patient in a slot.
 */
export function ChartLayout() {
  const { patientId = '' } = useParams();
  const { me } = useAuth();
  const role = me?.context.role.key ?? 'provider';
  const nav = useNavigate();
  const loc = useLocation();
  const { slotOf, openPatient, describe } = usePatientSlots();

  const pq = useQuery({ queryKey: qk.patient(patientId), queryFn: () => patientsApi.get(patientId), enabled: !!patientId });
  const aq = useQuery({ queryKey: qk.access(patientId), queryFn: () => patientsApi.accessCheck(patientId), enabled: !!patientId });
  const p = pq.data?.data;

  // Make sure a directly-visited chart occupies a slot.
  useEffect(() => {
    if (p && !slotOf(p.id)) openPatient({ id: p.id, display: p.name.display }, loc.pathname);
    if (p) describe(p.id, p.name.display, `${p.age.replace(' y', '')}${sexShort(p.sex)}${p.banner.encounter ? ' · ' + p.banner.encounter.place.split('·').pop()!.trim() : ''}`);
  }, [p, slotOf, openPatient, describe, loc.pathname]);

  const bg = aq.data?.activeBreakGlass;
  useChartPurpose(aq.data, purposeForRole(me?.context.roleId, WORKSPACES[role]?.purpose ?? 'TREATMENT'));

  const slot = (slotOf(patientId) ?? 1) as SlotNo;
  const section = loc.pathname.split('/')[3] ?? 'summary';
  const restricted = aq.data?.restrictedSections ?? [];
  const sections = chartSections(role).map((s) =>
    'id' in s && restricted.includes(s.id) ? { ...s, restricted: true, flag: { icon: 'shield', tone: 'sensitive', label: 'Some records restricted' } } : s,
  );

  const onIndexClick = (e: MouseEvent) => {
    const a = (e.target as HTMLElement).closest('a.ex-index-item') as HTMLAnchorElement | null;
    if (!a) return;
    e.preventDefault();
    nav(`/chart/${patientId}/${a.getAttribute('href')!.slice(1)}`);
  };

  if (pq.isPending) {
    return <ShellFrame banner={<div className="ex-banner" style={{ minHeight: 72 }}><Skeleton rows={2} cols={[40, 20, 20]} /></div>} canvas={<Skeleton rows={6} />} />;
  }
  if (pq.isError || !p) {
    // Policy denial (care relationship, sensitivity…): offer request access / break glass instead of a bare error.
    if (isApiError(pq.error, 'ETNAS_FORBIDDEN')) {
      if (section === 'rooming' && ['nurse', 'medical_assistant'].includes(role)) {
        return <ShellFrame canvas={<RoomingClaims patientId={patientId} />} />;
      }
      return <ShellFrame canvas={<ChartAccessDenied access={aq.data} patientId={patientId} slot={(slotOf(patientId) ?? 1) as SlotNo} />} />;
    }
    return <ShellFrame canvas={<ErrorState error={pq.error} onRetry={() => pq.refetch()} what="this chart" />} />;
  }
  const ds = toDsPatient(p);
  const value: ChartContextValue = {
    patientId, patient: p, etag: pq.data?.etag ?? null, access: aq.data ?? null, slot, ds, isRestricted: (s) => restricted.includes(s),
  };
  return (
    <ChartCtx.Provider value={value}>
      <ShellFrame
        banner={<PatientBannerX patient={p} slot={slot} />}
        index={
          <div onClick={onIndexClick} style={{ height: '100%' }}>
            <ChartIndex
              sections={sections}
              active={section}
              footer={
                restricted.length || bg ? (
                  bg ? (
                    <AccessNotice kind="break-glass" compact />
                  ) : (
                    <RestrictedNotice access={aq.data} patient={ds} patientId={patientId} compact slot={slot} />
                  )
                ) : undefined
              }
            />
          </div>
        }
        canvas={<Outlet key={patientId} />}
      />
    </ChartCtx.Provider>
  );
}
